/**
 * Zustand store for managing build mode state.
 */

import { create } from 'zustand';
import { BuildingType } from '@shared/constants/buildings';

type BuildCategory = 'tower' | 'wall' | 'barracks' | 'hero_spawner' | 'tech';

interface BuildModeState {
  // Actual build placement state
  isBuildModeActive: boolean;
  selectedBuildingType: BuildingType | null;

  // Build menu UI state
  isBuildMenuOpen: boolean;
  isTowerSubmenuOpen: boolean;
  activeCategory: BuildCategory | null;

  // Actions for build placement
  activateBuildMode: (buildingType: BuildingType) => void;
  deactivateBuildMode: () => void;
  toggleBuildMode: (buildingType: BuildingType) => void;

  // Actions for build menu UI
  openBuildMenu: () => void;
  closeBuildMenu: () => void;
  toggleBuildMenu: () => void;
  openTowerSubmenu: () => void;
  closeTowerSubmenu: () => void;
  toggleTowerSubmenu: () => void;
  setActiveCategory: (category: BuildCategory | null) => void;
}

export const useBuildModeStore = create<BuildModeState>((set) => ({
  isBuildModeActive: false,
  selectedBuildingType: null,

  isBuildMenuOpen: false,
  isTowerSubmenuOpen: false,
  activeCategory: null,

  activateBuildMode: (buildingType: BuildingType) =>
    set({
      isBuildModeActive: true,
      selectedBuildingType: buildingType,
    }),

  deactivateBuildMode: () =>
    set({
      isBuildModeActive: false,
      selectedBuildingType: null,
    }),

  toggleBuildMode: (buildingType: BuildingType) =>
    set((state) => {
      if (state.isBuildModeActive && state.selectedBuildingType === buildingType) {
        return { isBuildModeActive: false, selectedBuildingType: null };
      }
      return { isBuildModeActive: true, selectedBuildingType: buildingType };
    }),

  openBuildMenu: () =>
    set({
      isBuildMenuOpen: true,
    }),

  closeBuildMenu: () =>
    set({
      isBuildMenuOpen: false,
      isTowerSubmenuOpen: false,
      activeCategory: null,
    }),

  toggleBuildMenu: () =>
    set((state) => {
      if (state.isBuildMenuOpen) {
        return {
          isBuildMenuOpen: false,
          isTowerSubmenuOpen: false,
          activeCategory: null,
        };
      }
      return {
        isBuildMenuOpen: true,
      };
    }),

  openTowerSubmenu: () =>
    set({
      isTowerSubmenuOpen: true,
      activeCategory: 'tower',
    }),

  closeTowerSubmenu: () =>
    set({
      isTowerSubmenuOpen: false,
    }),

  toggleTowerSubmenu: () =>
    set((state) => ({
      isTowerSubmenuOpen: !state.isTowerSubmenuOpen,
      activeCategory: !state.isTowerSubmenuOpen ? 'tower' : state.activeCategory,
    })),

  setActiveCategory: (category: BuildCategory | null) =>
    set({
      activeCategory: category,
    }),
}));
